import { Code, Image as ImageIcon, Paperclip, X } from 'lucide-react';
import { useState } from 'react';

// 26 kategori - trend_topics_fetcher.py ile ayni
const CATEGORIES: { key: string; label: string; color: string }[] = [
  { key: 'ai-ml', label: 'AI & ML', color: '#8b5cf6' },
  { key: 'web-dev', label: 'Web Dev', color: '#3b82f6' },
  { key: 'blockchain', label: 'Blockchain', color: '#f59e0b' },
  { key: 'devops', label: 'DevOps', color: '#10b981' },
  { key: 'security', label: 'Security', color: '#ef4444' },
  { key: 'data-science', label: 'Data Science', color: '#06b6d4' },
  { key: 'mobile', label: 'Mobile', color: '#ec4899' },
  { key: 'open-source', label: 'Open Source', color: '#84cc16' },
  { key: 'philosophy', label: 'Philosophy', color: '#6366f1' },
  { key: 'psychology', label: 'Psychology', color: '#f97316' },
  { key: 'economics', label: 'Economics', color: '#14b8a6' },
  { key: 'art', label: 'Art', color: '#e879f9' },
  { key: 'music', label: 'Music', color: '#fb7185' },
  { key: 'literature', label: 'Literature', color: '#a78bfa' },
  { key: 'history', label: 'History', color: '#92400e' },
  { key: 'science', label: 'Science', color: '#0ea5e9' },
  { key: 'sports', label: 'Sports', color: '#22c55e' },
  { key: 'travel', label: 'Travel', color: '#f59e0b' },
  { key: 'food', label: 'Food', color: '#ef4444' },
  { key: 'environment', label: 'Environment', color: '#16a34a' },
  { key: 'society', label: 'Society', color: '#7c3aed' },
  { key: 'entrepreneurship', label: 'Startup', color: '#dc2626' },
  { key: 'self-improvement', label: 'Self Growth', color: '#d97706' },
  { key: 'career', label: 'Career', color: '#2563eb' },
  { key: 'health', label: 'Health', color: '#059669' },
  { key: 'education', label: 'Education', color: '#7c3aed' },
]

interface CreatePostCardProps {
  onSubmit: (content: string, tags: string[]) => Promise<void>
  user?: {
    id: number
    display_name: string
    avatar_url?: string
  }
}

export default function CreatePostCard({ onSubmit, user }: CreatePostCardProps) {
  const [content, setContent] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [showCategories, setShowCategories] = useState(false)

  const toggleCategory = (key: string) => {
    setSelectedCategories(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!content.trim()) { return }

    setIsSubmitting(true)
    try {
      // Hashtag'leri content'ten cek + secilen kategorileri ekle
      const hashtagRegex = /#(\w+)/g
      const contentTags = [...content.matchAll(hashtagRegex)].map(m => m[1])
      const allTags = [...new Set([...contentTags, ...selectedCategories])]

      await onSubmit(content, allTags)
      setContent('')
      setSelectedCategories([])
      setShowCategories(false)
    } catch (error) {
      console.error('Post creation failed:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div style={{
      background: 'var(--card-bg, #fff)',
      border: '1px solid var(--border-color, #e2e8f0)',
      borderRadius: '12px',
      padding: '20px'
    }}>
      <form onSubmit={(e) => { void handleSubmit(e) }}>
        {/* Avatar + Textarea */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '50%', flexShrink: 0,
            background: user?.avatar_url ? `url(${user.avatar_url})` : 'linear-gradient(135deg, #667eea, #764ba2)',
            backgroundSize: 'cover', backgroundPosition: 'center',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: '700', fontSize: '18px'
          }}>
            {!user?.avatar_url && (user?.display_name?.[0]?.toUpperCase() || 'U')}
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's on your mind? Share code, ask questions, discuss ideas..."
            disabled={isSubmitting}
            style={{
              flex: 1, border: '1px solid var(--border-color, #e2e8f0)',
              borderRadius: '8px', padding: '12px 16px', fontSize: '14px',
              color: 'var(--text-primary, #1a202c)', fontFamily: 'inherit',
              resize: 'none', minHeight: '80px', transition: 'border-color 0.2s',
              background: 'var(--card-bg, #fff)'
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = '#667eea' }}
            onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-color, #e2e8f0)' }}
          />
        </div>

        {/* Secilen kategoriler - chip'ler */}
        {selectedCategories.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px', paddingLeft: '60px' }}>
            {selectedCategories.map(key => {
              const cat = CATEGORIES.find(c => c.key === key)
              if (!cat) { return null }
              return (
                <span key={key} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '3px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: '600',
                  background: `${cat.color}18`, border: `1px solid ${cat.color}40`,
                  color: cat.color, cursor: 'pointer'
                }} onClick={() => toggleCategory(key)}>
                  #{cat.label}
                  <X size={11} />
                </span>
              )
            })}
          </div>
        )}

        {/* Kategori secici - toggle ile ac/kapat */}
        {showCategories && (
          <div style={{
            marginBottom: '12px', paddingLeft: '60px',
            display: 'flex', flexWrap: 'wrap', gap: '6px'
          }}>
            {CATEGORIES.map(cat => {
              const isSelected = selectedCategories.includes(cat.key)
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => toggleCategory(cat.key)}
                  style={{
                    padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '500',
                    cursor: 'pointer', transition: 'all 0.15s',
                    background: isSelected ? `${cat.color}18` : 'var(--body-bg, #f8fafc)',
                    border: isSelected ? `1px solid ${cat.color}60` : '1px solid var(--border-color, #e2e8f0)',
                    color: isSelected ? cat.color : 'var(--text-secondary, #64748b)'
                  }}
                >
                  {cat.label}
                </button>
              )
            })}
          </div>
        )}

        {/* Alt bar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingTop: '12px', borderTop: '1px solid var(--border-color, #f1f5f9)'
        }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            {/* Kategori toggle butonu */}
            <button
              type="button"
              onClick={() => setShowCategories(!showCategories)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
                cursor: 'pointer', transition: 'all 0.2s',
                background: showCategories ? 'rgba(102,126,234,0.1)' : 'var(--body-bg, #f8fafc)',
                border: showCategories ? '1px solid #667eea' : '1px solid var(--border-color, #e2e8f0)',
                color: showCategories ? '#667eea' : 'var(--text-secondary, #64748b)'
              }}
            >
              # Category
              {selectedCategories.length > 0 && (
                <span style={{
                  background: '#667eea', color: '#fff',
                  borderRadius: '50%', width: '16px', height: '16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '10px', fontWeight: '700'
                }}>{selectedCategories.length}</span>
              )}
            </button>
            <button
              type="button"
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
                cursor: 'pointer', transition: 'all 0.2s',
                background: 'var(--body-bg, #f8fafc)',
                border: '1px solid var(--border-color, #e2e8f0)',
                color: 'var(--text-secondary, #64748b)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f1f5f9'
                e.currentTarget.style.borderColor = '#cbd5e1'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--body-bg, #f8fafc)'
                e.currentTarget.style.borderColor = 'var(--border-color, #e2e8f0)'
              }}
            >
              <ImageIcon size={16} />
              Image
            </button>
            <button
              type="button"
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
                cursor: 'pointer', transition: 'all 0.2s',
                background: 'var(--body-bg, #f8fafc)',
                border: '1px solid var(--border-color, #e2e8f0)',
                color: 'var(--text-secondary, #64748b)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f1f5f9'
                e.currentTarget.style.borderColor = '#cbd5e1'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--body-bg, #f8fafc)'
                e.currentTarget.style.borderColor = 'var(--border-color, #e2e8f0)'
              }}
            >
              <Code size={16} />
              Code
            </button>
            <button
              type="button"
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
                cursor: 'pointer', transition: 'all 0.2s',
                background: 'var(--body-bg, #f8fafc)',
                border: '1px solid var(--border-color, #e2e8f0)',
                color: 'var(--text-secondary, #64748b)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f1f5f9'
                e.currentTarget.style.borderColor = '#cbd5e1'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--body-bg, #f8fafc)'
                e.currentTarget.style.borderColor = 'var(--border-color, #e2e8f0)'
              }}
            >
              <Paperclip size={16} />
              File
            </button>
          </div>
          <button
            type="submit"
            disabled={!content.trim() || isSubmitting}
            style={{
              padding: '8px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
              cursor: content.trim() ? 'pointer' : 'not-allowed', transition: 'all 0.2s',
              border: 'none',
              background: content.trim() ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f8fafc',
              color: content.trim() ? '#fff' : '#cbd5e1',
              opacity: content.trim() ? 1 : 0.5
            }}
          >
            {isSubmitting ? 'Sharing...' : 'Share'}
          </button>
        </div>
      </form>
    </div>
  )
}
